import { useState, useEffect, useMemo } from "react";
import { useLocation } from "react-router-dom";
import LZString from "lz-string";
import { useQueryClient } from "@tanstack/react-query";
import { useGetConfigInstanceId } from "../queries/appInstance";
import { useGetDefaultConfigs } from "../mutations/apps";
import { appKeys } from "../keys/appKeys";
import useSilentRefetch from "../utils/useSilentReload";
import { getDefaultConfig } from "../api/appInstance";
import useQueryParams from "../utils/useQueryParams";
import { useFontLoader } from "../utils/useFontLoader";
import {
  getEmbeddedAppConfig,
  persistEmbeddedAppConfig,
} from "../utils/getEmbeddedAppConfig";
import { getInstanceIdFromLocation } from "../utils/getInstanceIdFromLocation";
import { isDesignRenderRoute } from "../utils/isDesignRenderRoute";

function parseConfigsFromQuery(search: string): Record<string, unknown> | null {
  if (!search.includes("?configs=")) return null;
  const raw = search.split("?configs=")[1]?.split("&")[0] ?? "";
  if (!raw) return null;
  try {
    const decompressed = LZString.decompressFromEncodedURIComponent(raw);
    if (decompressed)
      return JSON.parse(decompressed) as Record<string, unknown>;
    const legacy = decodeURIComponent(escape(atob(raw)));
    return JSON.parse(legacy) as Record<string, unknown>;
  } catch {
    return null;
  }
}

interface ConfigData {
  app?: any;
  data?: any;
  configs?: any;
  appKey?: string;
  fonts?: any;
  instanceId: string;
  result?: any;
  themes?: any;
  id?: string;
  name?: string;
  success?: boolean;
}

interface UseAppConfigurationReturn {
  configData: ConfigData | undefined;
  params: any;
  hasInitialLoad: boolean;
  isLoading: boolean;
  isDataLoading: boolean;
  isError: boolean;
  fontLinks: HTMLLinkElement[];
  styleElements: HTMLStyleElement[];
}

function normalizeGalleryImagePath(item: unknown) {
  if (typeof item !== "string" || item.startsWith("{path}")) {
    return item;
  }

  try {
    const imageUrl = new URL(item, window.location.origin);
    const pathSegments = imageUrl.pathname.split("/");

    if (!pathSegments[2]) {
      return item;
    }

    return `{path}${imageUrl.pathname}`;
  } catch {
    return item;
  }
}

function normalizeGalleryConfigData<T extends ConfigData | undefined>(
  config: T,
  shouldNormalize: boolean,
): T {
  if (
    !shouldNormalize ||
    !config ||
    !Array.isArray(config?.configs?.gallery?.value)
  ) {
    return config;
  }

  return {
    ...config,
    configs: {
      ...config.configs,
      gallery: {
        ...config.configs.gallery,
        value: config.configs.gallery.value.map(normalizeGalleryImagePath),
      },
    },
  } as T;
}

function parseWeightToNumbers(weight: string | null | undefined): number[] {
  if (weight == null || weight === "") {
    return [400];
  }
  const names: Record<string, number> = {
    thin: 100,
    "100": 100,
    extralight: 200,
    "200": 200,
    light: 300,
    "300": 300,
    regular: 400,
    normal: 400,
    "400": 400,
    medium: 500,
    "500": 500,
    semibold: 600,
    demibold: 600,
    "600": 600,
    bold: 700,
    "700": 700,
    extrabold: 800,
    "800": 800,
    black: 900,
    "900": 900,
  };
  const parts = weight
    .split(";")
    .map((s) => s.trim().toLowerCase())
    .filter(Boolean);
  if (parts.length === 0) return [400];
  const numbers = parts
    .map((p) => (names[p] !== undefined ? names[p] : Number(p)))
    .filter((n) => !Number.isNaN(n) && n >= 100 && n <= 900);
  const unique = numbers.filter((n, i) => numbers.indexOf(n) === i);
  return unique.length > 0 ? unique.sort((a, b) => a - b) : [400];
}

export const useAppConfiguration = (): UseAppConfigurationReturn => {
  const location = useLocation();
  const isGalleryShell = location.pathname.endsWith("gallery.html");
  const isDesignerRender = isDesignRenderRoute(location.pathname);
  const embeddedConfig = useMemo(() => getEmbeddedAppConfig(), []);
  const embeddedInstanceId =
    (embeddedConfig?.instanceId as string | undefined) ||
    (embeddedConfig?.id as string | undefined) ||
    "";
  const instanceId = getInstanceIdFromLocation(location) || embeddedInstanceId;
  const isEmbeddedConfig = Boolean(embeddedConfig);
  const { getQueryParam } = useQueryParams();
  const layout = getQueryParam("layout");
  const duration = getQueryParam("duration");
  const menu = getQueryParam("menu");
  const show_nextweek = menu === "next" ? true : undefined;

  const [configData, setConfigData] = useState<ConfigData | undefined>(() => {
    if (!embeddedConfig) {
      return undefined;
    }

    return normalizeGalleryConfigData({
      ...embeddedConfig,
      instanceId,
    } as ConfigData, isGalleryShell);
  });
  const [params, setParams] = useState<any>(null);
  const [hasInitialLoad, setHasInitialLoad] = useState(Boolean(embeddedConfig));

  const fontNames = useMemo(() => {
    if (!configData?.configs) {
      return [];
    }
    return [
      configData.configs.primary_font?.value,
      configData.configs.secondary_font?.value,
    ].filter(Boolean);
  }, [configData?.configs]);

  const fontWeights = useMemo(() => {
    const out: Record<string, number[]> = {};
    if (!configData?.configs) return out;
    const defaultFonts: Array<{ value?: string; weight?: string | null }> =
      configData?.fonts?.default ?? [];
    const getWeightForValue = (
      fontValue: string,
      fallbackWeight: string | null | undefined,
    ): number[] => {
      const fromDefault = defaultFonts.find(
        (f) => f.value?.toLowerCase() === fontValue.toLowerCase(),
      );
      return parseWeightToNumbers(fromDefault?.weight ?? fallbackWeight);
    };
    const primary = configData.configs.primary_font;
    const secondary = configData.configs.secondary_font;
    if (primary?.value) {
      out[primary.value] = getWeightForValue(primary.value, primary.weight);
    }
    if (secondary?.value && secondary.value !== primary?.value) {
      out[secondary.value] = getWeightForValue(
        secondary.value,
        secondary.weight,
      );
    }
    return out;
  }, [configData?.configs, configData?.fonts?.default]);
  const { fontLinks, styleElements } = useFontLoader({
    fontNames,
    fontWeights,
    brandingFonts: configData?.fonts,
  });

  const {
    data,
    isLoading: isDataLoading,
    isError,
    refetch,
  } = useGetConfigInstanceId(
    {
      instanceId,
      layout: layout || undefined,
      duration: duration || undefined,
      show_nextweek,
    },
    {
      enabled:
        !location.search.includes("?configs=") &&
        !hasInitialLoad &&
        !isEmbeddedConfig &&
        !isDesignerRender &&
        Boolean(instanceId),
    },
  );

  const queryClient = useQueryClient();
  const { mutate: getRenderData, isLoading } = useGetDefaultConfigs({
    onSuccess(data) {
      setConfigData((prevConfigData: ConfigData | undefined) =>
        normalizeGalleryConfigData({
          ...prevConfigData,
          app: data.app,
          data: data.data,
          instanceId,
          result: data.result,
          themes: data.themes,
        } as ConfigData, isGalleryShell)
      );

      queryClient.invalidateQueries(appKeys.item(instanceId));
    },
  });

  // Handle error retry
  useEffect(() => {
    if (isEmbeddedConfig || isDesignerRender) {
      return;
    }

    const timeoutId = setTimeout(() => {
      if (!location.search.includes("?configs=") && isError) {
        refetch();
      }
    }, 60000);

    return () => clearTimeout(timeoutId);
  }, [
    data,
    refetch,
    isError,
    isEmbeddedConfig,
    isDesignerRender,
    location.search,
  ]);

  // Handle data from API
  useEffect(() => {
    if (isEmbeddedConfig || isDesignerRender) {
      return;
    }

    if (!location.search.includes("?configs=") && data) {
      setConfigData(
        normalizeGalleryConfigData({
          ...data,
          instanceId,
        } as ConfigData, isGalleryShell)
      );
      setHasInitialLoad(true);
    }
  }, [
    data,
    isEmbeddedConfig,
    isDesignerRender,
    location.search,
    instanceId,
    isGalleryShell,
  ]);

  useEffect(() => {
    const appKey = configData?.appKey || configData?.app?.key;
    const shouldPersistForSavedGallery =
      location.pathname.endsWith("gallery.html") &&
      appKey === "image_gallery" &&
      Boolean(configData);

    if (!shouldPersistForSavedGallery || !configData) {
      return;
    }

    persistEmbeddedAppConfig({
      ...configData,
      instanceId,
    });
  }, [configData, instanceId, location.pathname]);

  // Handle configs from URL (supports LZ-compressed and legacy base64 format)
  useEffect(() => {
    if (location.search.includes("?configs=")) {
      try {
        const result = parseConfigsFromQuery(location.search);
        if (!result) return;
        const { appKey, configs, appId, authentication, calendars, fonts } =
          result as any;

        const dataSend = {
          appKey: Number(appId!),
          configs: {
            instanceId,
            configs: configs,
            authenticationId: authentication?.id,
            calendars: calendars,
          },
          instanceId,
          result: result.result,
          themes: result.themes,
        };

        if (appId) {
          getRenderData(dataSend as any);
          setParams(dataSend);
          setHasInitialLoad(true);

          setConfigData(
            normalizeGalleryConfigData({
              configs,
              appKey,
              fonts,
              instanceId,
              result: result.result,
              themes: result.themes,
            } as ConfigData, isGalleryShell)
          );
        }
      } catch (error) {
        console.error("Error parsing configs from URL:", error);
      }
    }
  }, [getRenderData, location.search, instanceId, isGalleryShell]);

  const silentRefetchData = useSilentRefetch({
    fetchDataFunction: getDefaultConfig,
    params: params,
    interval: isEmbeddedConfig || isDesignerRender
      ? -1
      : configData?.data &&
          ((Array.isArray(configData.data) && configData.data.length === 0) ||
            (typeof configData.data === "object" &&
              !Array.isArray(configData.data) &&
              Object.keys(configData.data).length === 0))
        ? 60000
        : -1,
    dataFromConfig: configData,
  });

  const autoReloadTimer = configData?.app?.auto_reload_timer;
  const interval = Math.min(autoReloadTimer * 1000, 300000);

  const autoReloadRefetchData = useSilentRefetch({
    fetchDataFunction: getDefaultConfig,
    params: params,
    interval: isEmbeddedConfig || isDesignerRender
      ? -1
      : configData?.app?.auto_reload === true
        ? interval || 300000
        : -1,
    dataFromConfig: configData,
  });

  useEffect(() => {
    if (
      silentRefetchData &&
      silentRefetchData.data &&
      ((Array.isArray(silentRefetchData.data) &&
        silentRefetchData.data.length > 0) ||
        (typeof silentRefetchData.data === "object" &&
          !Array.isArray(silentRefetchData.data) &&
          Object.keys(silentRefetchData.data).length > 0))
    ) {
      setConfigData(
        normalizeGalleryConfigData(
          silentRefetchData as ConfigData,
          isGalleryShell,
        )
      );
    }
  }, [silentRefetchData, isGalleryShell]);

  useEffect(() => {
    if (
      autoReloadRefetchData &&
      autoReloadRefetchData.data &&
      configData?.app?.auto_reload === true
    ) {
      // Update configData with new data while preserving the structure
      setConfigData((prevConfigData: ConfigData | undefined) =>
        normalizeGalleryConfigData({
          ...prevConfigData,
          data: autoReloadRefetchData.data,
          // Preserve all other properties from the refetch data
          ...autoReloadRefetchData,
        } as ConfigData, isGalleryShell)
      );
    }
  }, [
    autoReloadRefetchData,
    configData?.app?.auto_reload,
    configData?.app,
    isGalleryShell,
  ]);

  const resolvedIsDataLoading =
    isEmbeddedConfig || isDesignerRender ? false : isDataLoading;
  const resolvedIsError = isEmbeddedConfig || isDesignerRender ? false : isError;

  return {
    configData,
    params,
    hasInitialLoad,
    isLoading,
    isDataLoading: resolvedIsDataLoading,
    isError: resolvedIsError,
    fontLinks,
    styleElements,
  };
};
